import { useCallback, useState } from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import { scrollSpy } from '../utils/dom'
import { cx, slugify } from '../utils/strings'
import CtaLink, { buttonStyles } from './ui/cta-link'
import Collapsible from './ui/collapsible'
import AngleIcon from '@/icons/bwi/angle-up.svg'

export default function TocNav({
  id = 'toc-nav',
  title = 'On this page',
  sections = [],
  scrollToOffset = 0,
}): JSX.Element | null {
  if (sections) scrollSpy(id)
  const [isOpen, setIsOpen] = useState(false)

  const renderSections = () => {
    return (
      <ul className={'px-4 py-2 list-none md:p-0'}>
        {sections
          ?.filter((section) => section.__typename === 'ContentfulComponentRichText' && section.body)
          .map((section) =>
            documentToReactComponents(
              {
                nodeType: BLOCKS.DOCUMENT,
                data: {},
                content: section.body.content.filter((chunk) =>
                  [BLOCKS.HEADING_2, BLOCKS.HEADING_3].includes(chunk.nodeType)
                ),
              },

              {
                renderNode: {
                  [BLOCKS.HEADING_2]: useCallback(
                    (node, children) => (
                      <li className="pt-1 pb-2 leading-tight">
                        <CtaLink
                          style={buttonStyles.btnText}
                          backgroundColor="white"
                          to={`#${slugify(children?.[0]?.props?.children || children?.[0])}`}
                          className="[&.active]:text-indigoBlue dark:[&.active]:text-white text-[15px] xl:text-lg"
                          scrollToOffset={scrollToOffset}
                        >
                          {children}
                        </CtaLink>
                      </li>
                    ),
                    []
                  ),
                  [BLOCKS.HEADING_3]: useCallback(
                    (node, children) => (
                      <li className="pb-1 ml-2 text-base text-primaryBlue leading-tight">
                        <CtaLink
                          style={buttonStyles.btnText}
                          backgroundColor="white"
                          className=" [&.active]:text-indigoBlue dark:[&.active]:text-white text-sm xl:text-base"
                          to={`#${slugify(children?.[0]?.props?.children || children?.[0])}`}
                          scrollToOffset={scrollToOffset}
                        >
                          {children}
                        </CtaLink>
                      </li>
                    ),
                    []
                  ),
                },
              }
            )
          )}
      </ul>
    )
  }
  return sections.some(
    (section) =>
      section.__typename === 'ContentfulComponentRichText' &&
      section.body &&
      section.body.content.some((chunk) => [BLOCKS.HEADING_2, BLOCKS.HEADING_3].includes(chunk.nodeType))
  ) ? (
    <nav id={id} className="mt-3 md:my-6">
      <div className="hidden md:block">
        <span className="block font-bold mb-2">On this page</span>
        {renderSections()}
      </div>

      <div className="md:hidden border bg-secondary-50 rounded-xl">
        <button
          className={cx(
            'flex w-full justify-between text-primaryBlue py-2 px-4 font-semibold text-sm',
            isOpen && 'shadow-xs'
          )}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>On this page</span>
          <AngleIcon className={cx('ml-3 w-4 shrink-0 mt-1 h-3 transition duration-200', isOpen ? '' : `rotate-180`)} />
        </button>

        <Collapsible isOpen={isOpen}>{renderSections()}</Collapsible>
      </div>
    </nav>
  ) : null
}
